/**
 * The full-text query language of the OCR and transcript searches: the spec calls `filterOcr` and
 * `filterTranscription` "a boolean full text search" — MySQL InnoDB `MATCH … AGAINST (… IN BOOLEAN
 * MODE)`. Every operator form below is accepted by `/mail`, `/fax/receive` and `/phone` (sandbox,
 * 2026-10-23, `_fe225-fts-probe.js`). What each MATCHES is MySQL's documented behaviour, not measured:
 * no sandbox account has mail, faxes or calls to match against.
 *
 * Pure, so the query builder, the chip strip under the search box and their tests share one parser.
 * Ported from the design's `FtsSearch.jsx`; its client-side matcher is not — the demo has no full text.
 */

/** MySQL's defaults: `innodb_ft_min_token_size` and the InnoDB stopword list. The server's own settings are not known. */
const MIN_LENGTH = 3;
const STOPWORDS = new Set(
    'a about an are as at be by com de en for from how i in is it la of on or that the this to was what when where who will with und www'.split(' '),
);
/** `+` must, `-` must not, `''` optional, `>` rank higher, `<` / `~` rank lower. */
export type FileCabinetFtsOperator = '' | '+' | '-' | '>' | '<' | '~';

/** Where a term sits in the query it was read from: `query.slice(start, end)` is its `raw`. */
interface FileCabinetFtsSpan {
    raw: string;
    op: FileCabinetFtsOperator;
    start: number;
    end: number;
}

export type FileCabinetFtsTerm =
    | (FileCabinetFtsSpan & { kind: 'word'; text: string; prefix: boolean; ignored: boolean })
    | (FileCabinetFtsSpan & { kind: 'phrase'; text: string })
    | (FileCabinetFtsSpan & { kind: 'near'; text: string; distance: number })
    | (FileCabinetFtsSpan & { kind: 'group'; text: string; inner: FileCabinetFtsTerm[] });

export interface FileCabinetFtsParse {
    terms: FileCabinetFtsTerm[];
    /** What may not search the way it reads — said, not silently corrected. */
    warnings: string[];
}

/**
 * The words MySQL indexes: runs of letters and digits in ANY alphabet. An ASCII-only split read
 * "García" as "garc" and "a" — on a search mostly used for names, two wrong words.
 */
const words = (text: string): string[] => text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
const skipped = (word: string): boolean => [...word].length < MIN_LENGTH || STOPWORDS.has(word);
const quoted = (list: string[]): string => list.map(w => `“${w}”`).join(', ');
const OPERATORS = '+-~<>';

/** Where the group opened at `open` closes: brackets balanced, quoted text skipped. The end of the text if never. */
function groupEnd(query: string, open: number): number {
    let depth = 0;
    let inQuote = false;
    for (let i = open; i < query.length; i++) {
        const c = query[i];
        if (c === '"') {
            inQuote = !inQuote;
        } else if (!inQuote && c === '(') {
            depth++;
        } else if (!inQuote && c === ')' && --depth === 0) {
            return i;
        }
    }
    return query.length;
}

/**
 * The query's terms, read left to right: an optional operator, then a "phrase" (with `@N`, a
 * proximity), a (group) — nested groups included — or a word. Each keeps its span, so a chip can
 * remove exactly itself.
 */
export function fileCabinetFtsParse(query: string): FileCabinetFtsParse {
    const terms: FileCabinetFtsTerm[] = [];
    const warnings: string[] = [];
    let i = 0;
    while (i < query.length) {
        if (/\s/.test(query[i])) {
            i++;
            continue;
        }
        if (query[i] === ')') {
            warnings.push('A ")" has no "(" before it — it is ignored.');
            i++;
            continue;
        }
        const start = i;
        const op = (OPERATORS.includes(query[i]) && i + 1 < query.length && !/\s/.test(query[i + 1]) ? query[i++] : '') as FileCabinetFtsOperator;
        if (query[i] === '"') {
            const close = query.indexOf('"', i + 1);
            const phrase = query.slice(i + 1, close < 0 ? query.length : close);
            i = close < 0 ? query.length : close + 1;
            const distance = /^\s*@(\d+)/.exec(query.slice(i));
            if (distance) {
                i += distance[0].length;
            }
            const short = words(phrase).filter(skipped);
            if (short.length) {
                warnings.push(`"${phrase}" has ${quoted(short)} — short words and common words are usually not indexed, which can stop the phrase matching.`);
            }
            const raw = query.slice(start, i);
            terms.push(
                distance
                    ? { kind: 'near', raw, op, start, end: i, text: phrase, distance: Number(distance[1]) }
                    : { kind: 'phrase', raw, op, start, end: i, text: phrase },
            );
        } else if (query[i] === '(') {
            const close = groupEnd(query, i);
            const group = query.slice(i + 1, close);
            if (close >= query.length) {
                warnings.push(`"(${group}" has no closing ")".`);
            }
            i = Math.min(close + 1, query.length);
            const inner = fileCabinetFtsParse(group);
            warnings.push(...inner.warnings);
            terms.push({ kind: 'group', raw: query.slice(start, i), op, start, end: i, text: group, inner: inner.terms });
        } else {
            while (i < query.length && !/[\s()"]/.test(query[i])) {
                i++;
            }
            const raw = query.slice(start, i);
            let text = raw.slice(op.length);
            if (text.startsWith('*')) {
                warnings.push(`“${text}” starts with * — only a trailing * (a prefix) is supported.`);
                text = text.replace(/^\*+/, '');
            }
            const prefix = text.endsWith('*');
            text = text.replace(/\*+$/, '');
            const parts = words(text);
            if (parts.length > 1) {
                warnings.push(`“${text}” is read as ${parts.map(p => `“${p}”`).join(' and ')} — a "-" inside a word can act as "must not". Quote it to search it as one: "${parts.join(' ')}"`);
            }
            const normal = parts.join(' ') || text.toLowerCase();
            const ignored = !prefix && parts.length <= 1 && skipped(normal);
            if (ignored) {
                warnings.push(
                    `“${normal}” is likely ignored — ${[...normal].length < MIN_LENGTH ? `words under ${MIN_LENGTH} letters` : 'common words'} are usually not indexed.`,
                );
            }
            terms.push({ kind: 'word', raw, op, start, end: i, text: normal, prefix, ignored });
        }
    }
    const live = terms.filter(t => !(t.kind === 'word' && t.ignored));
    if (live.length && live.every(t => t.op === '-')) {
        warnings.push('Only "must not" terms: this matches nothing. Add a word to look for.');
    }
    return { terms, warnings };
}

/** The builder's fields. Each holds space-separated words (a quoted phrase counts as one). */
export interface FileCabinetFtsModel {
    all: string;
    any: string;
    phrase: string;
    near: string;
    distance: number;
    not: string;
    boost: string;
    lower: string;
    /** Anything the fields cannot hold, kept verbatim so opening the builder never loses part of a query. */
    extra: string;
}

export const FILE_CABINET_FTS_EMPTY: FileCabinetFtsModel = {
    all: '',
    any: '',
    phrase: '',
    near: '',
    distance: 8,
    not: '',
    boost: '',
    lower: '',
    extra: '',
};

/** A query, spread over the builder's fields. */
export function fileCabinetFtsModel(query: string): FileCabinetFtsModel {
    const all: string[] = [];
    const any: string[] = [];
    const not: string[] = [];
    const boost: string[] = [];
    const lower: string[] = [];
    const extra: string[] = [];
    let phrase = '';
    let near = '';
    let distance = FILE_CABINET_FTS_EMPTY.distance;
    let anyTaken = false;
    const terms = fileCabinetFtsParse(query).terms;
    // Bare terms mean "at least one of these" only when nothing is required, and only the "At least one
    // of" field can say that — for words. Beside a `+` term they only rank; beside an optional phrase or
    // group they are alternatives to it; and `>word`, `<word`, `~word` are optional terms too — they
    // change the ranking, not whether a record matches — so beside them a bare word is one alternative
    // among several. In each case moving the bare words into a field would change which records match
    // (`denial >appeal` would require "denial"), so they are kept as typed.
    const required = terms.some(t => t.op === '+');
    const optionalWordsOnly = terms.filter(t => t.op !== '+' && t.op !== '-').every(t => t.kind === 'word' && t.op === '');
    for (const term of terms) {
        // As typed — case, accents and a trailing * kept; only the operator read off.
        const typed = term.raw.slice(term.op.length);
        if (term.kind === 'group') {
            // Only a group of plain alternatives fits the field; one with operators or nested groups is kept whole.
            const plain = term.inner.every(t => t.op === '' && (t.kind === 'word' || t.kind === 'phrase'));
            if (!anyTaken && term.op === '+' && plain && term.inner.length > 0) {
                anyTaken = true;
                any.push(...term.inner.map(t => t.raw));
            } else {
                extra.push(term.raw);
            }
        } else if (term.kind === 'near' && term.op === '+' && !near) {
            near = term.text;
            distance = term.distance;
        } else if (term.kind === 'phrase' && term.op === '+' && !phrase) {
            phrase = term.text;
        } else if (term.op === '-') {
            // A phrase or proximity keeps its quotes and distance.
            not.push(typed);
        } else if (term.kind !== 'word') {
            extra.push(term.raw);
        } else if (term.op === '+') {
            all.push(typed);
        } else if (term.op === '>') {
            boost.push(typed);
        } else if (term.op === '~') {
            lower.push(typed);
        } else if (!required && optionalWordsOnly && !anyTaken) {
            any.push(typed);
        } else {
            extra.push(term.raw);
        }
    }
    return {
        all: all.join(' '),
        any: any.join(' '),
        phrase,
        near,
        distance,
        not: not.join(' '),
        boost: boost.join(' '),
        lower: lower.join(' '),
        extra: extra.join(' '),
    };
}

/**
 * Space- or comma-separated (people type "a, b"), a quoted phrase (with any `@N`) as one item. A comma
 * would be a 400 on the wire anyway (`searchValue`); here it never gets into the query at all.
 */
const items = (text: string): string[] => text.match(/"[^"]*"(?:\s*@\d+)?|[^\s,]+/g) ?? [];
/** A phrase field takes words, not quotes: a typed quote would end the phrase early. */
const unquote = (text: string): string => text.replace(/"/g, '').trim();

/** The builder's fields, as one query. */
export function fileCabinetFtsQuery(model: FileCabinetFtsModel): string {
    const phrase = unquote(model.phrase);
    const near = unquote(model.near);
    const distance = Math.max(1, Math.round(model.distance) || FILE_CABINET_FTS_EMPTY.distance);
    const any = items(model.any);
    return [
        ...items(model.all).map(w => `+${w}`),
        phrase && `+"${phrase}"`,
        near && `+"${near}" @${distance}`,
        any.length > 0 && `+(${any.join(' ')})`,
        ...items(model.not).map(w => `-${w}`),
        ...items(model.boost).map(w => `>${w}`),
        ...items(model.lower).map(w => `~${w}`),
        model.extra.trim(),
    ]
        .filter(Boolean)
        .join(' ');
}

/** The query without one of its top-level terms — a chip's remove button. */
export function fileCabinetFtsWithout(query: string, term: { start: number; end: number }): string {
    // By the term's own span: a text search for it would cut "auth" out of "authorization".
    return `${query.slice(0, term.start)} ${query.slice(term.end)}`.replace(/\s+/g, ' ').trim();
}

/** How a term reads on its chip: the tag says what it does, the text what it looks for. */
export function fileCabinetFtsLabel(term: FileCabinetFtsTerm): { tag: string; text: string } {
    const base: Record<FileCabinetFtsOperator, string> = { '+': 'Must', '-': 'Not', '': 'Any', '>': 'Rank up', '<': 'Rank down', '~': 'Rank down' };
    switch (term.kind) {
        case 'word':
            return {
                tag: term.ignored ? 'Ignored' : term.prefix ? `${base[term.op]} · prefix` : base[term.op],
                // As typed: "García", not the tokenizer's lower-case "garcía".
                text: term.raw.slice(term.op.length),
            };
        case 'phrase':
            return { tag: term.op === '-' ? 'Not phrase' : 'Phrase', text: `"${term.text}"` };
        case 'near':
            return { tag: term.op === '-' ? 'Not near' : `Within ${term.distance}`, text: `"${term.text}"` };
        case 'group':
            return { tag: term.op === '+' ? 'One of' : base[term.op], text: term.inner.map(t => t.raw).join(' | ') };
    }
}

/** The syntax, for the builder's reference. MySQL boolean mode. */
export const FILE_CABINET_FTS_REFERENCE: [string, string][] = [
    ['+word', 'Must contain'],
    ['-word', 'Must not contain'],
    ['word', 'Optional — matches if any optional word is there'],
    ['auth*', 'Starts with — authorization, authorized…'],
    ['"exact phrase"', 'These words, in this order'],
    ['"a b" @8', 'These words within 8 words of each other'],
    ['+(a b)', 'Must contain at least one of'],
    ['>word', 'Rank higher (does not filter)'],
    ['~word, <word', 'Rank lower (does not filter)'],
];
